/* eslint-disable @typescript-eslint/ban-ts-comment */
import {Dispatch, Action} from "@reduxjs/toolkit"
import {SyntheticEvent} from "react"
import logger from "@next/core-logger"
import Cookies from "js-cookie"
import type {AxiosError} from "axios"
import {ConsentCookie, CurrencyConfiguration} from "@next/utils"
import {INFINITE_SCROLL} from "../../models/PageLoadTrigger"
import getOpenFiltersFromFacets from "../../utils/getOpenFiltersFromFacets"
import {getFiltersWithoutPriceAndSort} from "../../utils/getFiltersFromMerchandisedPath"
import {AreaSegmentOptions} from "../../utils/getAreaSegment/constants"
import {getAreaSegment} from "../../utils/getAreaSegment"
import createItemsCache from "../../utils/createItemsCache"
import {getCategorySearchUrlSegment} from "../../utils/seo/getCategorySearchUrlSegment"
import {State} from "../../models/State"
import {setViewAllCloseAction} from "../viewAllModal"
import {ProductItem} from "../../models/Product"
import {
    MapProductsFragmentJsonToItemsResponse,
    MapProductsFragmentJsonToItemsResponseItems,
    MapProductsFragmentToItemsResponse,
    mapProductsFragmentJsonToItems,
    mapProductsFragmentToItems,
} from "../../utils/productsFragment"
import {getProducts} from "../../api/getProducts"
import {isRedirectResponse} from "../../models/searchApi/typeGuards"
import {FilterCookie} from "../../models/cookie"
import {PREV_PAGE, NEXT_PAGE, FILTERING, NO_LAZYLOAD} from "../../models/Lazyload"
import setFilterCookie from "../../utils/setFilterCookie"
import mapApiDataToState from "../../utils/mapApiDataToState"
import {
    ExternalSearchApiResponse,
    GenericSearchResponse,
    pageLoadTriggerType,
    SearchApiResponse,
} from "../../models/searchApi"
import {FilterPriceApiResponse, Filters, FilterPrice} from "../../models/Filter"
import {FacetsState} from "../../models/FacetsState"
import {AdvertsContent, Breakpoints, PageRange, SearchState} from "../../models/SearchState"
import {
    applyIncompatibleWithToFacets as deselectAllFiltersIncompatibleWith,
    getInitialPage,
    formatSearchState,
    getLazyloadItemIndexFrom,
    selectSubsequentItemsPerPage,
    extractTemplateOverride,
    generateItemsString,
    selectInitialItemsPerPage,
    initialAndSubsequentPageSizeEqual,
} from "./utils"
import {getProductsFragment} from "../../api/getProductsFragment"
import {getProductsFragmentByPage} from "../../api/getProductsFragmentByPage"
import {isFilterFacet, isPriceFilter} from "../../models/Filter/typeGuards"
import {getWindow, scrollToTop} from "../../utils/window"
import {debounceAndGetCancelToken} from "../../utils/debounceAndGetCancelToken"
import {selectedFacetFilterTitle} from "../../utils/selectedFacetFilterTitle"
import {build} from "../../utils/urlBuilder"
import {
    FILTER_SETTINGS_COOKIE,
    HISTORY_EVENT,
    TOKEN_CANCELLATION_FLAG,
    SearchApiRequestTypes,
    CATEGORY_FILTERS_REGEX,
    MAX_PAGE_TO_LOAD,
    ESISeparator,
    FILTERS_SHOW_VIEW_MORE_COUNT_EXTENDED_HORIZONTAL,
    SCORE,
} from "../../config/constants"
import {populateCategoryQuickLinks} from "../categoryQuickLinks"
import {selectSeoMetadata} from "../../utils/seo/selectSeoMetadata"
import {updateDocumentSeoMetadata} from "../../utils/seo/updateDocumentSeoMetadata"
import {assignRequestDuck} from "../request"
import redirectErrorPage from "../../utils/redirectErrorPage"
import {syncTabbedFiltersFromSearch} from "../tabbedFilters"
import {getMerchandisedSEOFiltersHTML, getSEOFiltersHTML} from "../../utils/getSEOFiltersHTML"
import indexFilters, {indexMerchandiseURLFilters} from "../../utils/seo/indexFilters"

import PublishProductSummaryTrackPage from "../../events/publishProductSummaryTrackPage"
import TrackFacetSelection from "../../events/trackEvent/events/trackFacetSelection"
import TrackFacetDeselection from "../../events/trackEvent/events/trackFacetDeselection"
import TrackCTATabbedFilters from "../../events/trackEvent/events/trackCTATabbedFilters"
import TrackPriceFilterChange from "../../events/trackEvent/events/trackPriceFilterChange"
import TrackIsViewMoreTriggeredFilter from "../../events/trackEvent/events/trackIsViewMoreTriggeredFilter"
import TrackIsViewLessTriggeredFilter from "../../events/trackEvent/events/trackIsViewLessTriggeredFilter"
import TrackFilterExpanded from "../../events/trackEvent/events/trackFilterExpanded"

import {pushSearchResultsEvent} from "../../utils/pushSearchResultsEvent"
import {updateSaleThunk} from "../sale"
import {getCategorySegmentFromUrl} from "../../utils/getCategoryFromUrl"
import {removeCookieFromCurrentDomain} from "../../utils/removeCookieFromDomain"
import {getProductFragmentsById} from "../../api/getProductFragmentsById"
import {getStaticProductFragmentsById} from "../../api/getStaticProductFragmentsById"
import {removeScriptTags} from "../../utils/scriptTags"
import {TemplateSettingsDuckState} from "../../models/templates"
import {mapItems} from "../../utils/mapItems"
import {updateImageRatio, updateProductSummary, updateProductSummaryItemsData} from "../productSummary"
import extractImageRatio from "../../utils/extractImageRatio"
import {getProductSummaryAggregation} from "../../api/getProductSummaryAggregation"
import breakpointToViewportSize from "../../utils/breakpointToViewportSize"
import {getItemsStringWithoutHTML} from "../../utils/getItemsStringWithoutHTML"
import axios from "../../utils/axios"
import env from "../../config/env"
import {urlSanitiser} from "../../utils/httpUrlTrimmer"
import {getSearchResults} from "../../api/getSearchResults"
import {getAdsConfig} from "../../api/getAdsConfig"
import {overrideTemplateByCategory} from "../../utils/overrideTemplateByCategory"
import {makeStore} from ".."

export const SET_FACET = "SET_FACET"
export const SET_CLEAR_TYPE_FILTER = "SET_CLEAR_TYPE_FILTER"
export const SET_CLEAR_ALL_FILTER = "SET_CLEAR_ALL_FILTER"
export const SET_CLEAR_PRICE_FILTER = "SET_CLEAR_PRICE_FILTER"
export const SET_ALL_FACETS = "SET_ALL_FACETS"
export const SET_KEY_FACETS = "SET_KEY_FACETS"
export const SET_SEARCH_DATA = "SET_SEARCH_DATA"
export const SET_FILTER_IS_OPEN = "SET_FILTER_IS_OPEN"
export const SET_SELECTED_SORTING = "SET_SELECTED_SORTING"
export const NEXT_PAGE_ITEMS_READY = "NEXT_PAGE_ITEMS_READY"
export const PREVIOUS_PAGE_ITEMS_READY = "PREVIOUS_PAGE_ITEMS_READY"
export const SET_PAGE_ITEMS = "SET_PAGE_ITEMS"
export const SET_NEW_PAGE_ITEMS = "SET_NEW_PAGE_ITEMS"
export const SET_IS_FETCHING_PAGE_ITEMS = "SET_IS_FETCHING_PAGE_ITEMS"
export const SET_STARTING_PAGE = "SET_STARTING_PAGE"
export const SET_IS_FETCHING_NEXT_PAGE = "SET_IS_FETCHING_NEXT_PAGE"
export const SET_IS_FETCHING_PREVIOUS_PAGE = "SET_IS_FETCHING_PREVIOUS_PAGE"
export const SET_SELECTED_PRICE_FILTER = "SET_SELECTED_PRICE_FILTER"
export const SET_INITIAL_PAGE_RANGE_FROM_PAGE = "SET_INITIAL_PAGE_RANGE_FROM_PAGE"
export const SET_DEBOUNCE_TIME = "SET_DEBOUNCE_TIME"
export const SET_INJECTED_ADS_ENABLED = "SET_INJECTED_ADS_ENABLED"
export const SET_INJECTED_ADS_HIDDEN_FOR_TIMEOUT = "SET_INJECTED_ADS_HIDDEN_FOR_TIMEOUT"
export const SET_HIDE_FOOTER_UNTIL_LAST_PAGE = "SET_HIDE_FOOTER_UNTIL_LAST_PAGE"
export const SET_ENABLE_STATIC_ESI_ENDPOINT = "SET_ENABLE_STATIC_ESI_ENDPOINT"
export const SET_SINGLE_OPTION_FACET_LIST = "SET_SINGLE_OPTION_FACET_LIST"
export const SET_SINGLE_FACET_FILTERS_TO_BE_PRESERVED_IN_THE_URL = "SET_SINGLE_FACET_FILTERS_TO_BE_PRESERVED_IN_THE_URL"
export const FIX_FACETS = "FIX_FACETS"
export const UNFIX_FACETS = "UNFIX_FACETS"
export const ASSIGN_SEARCH_STATE = "ASSIGN_SEARCH_STATE"
export const SET_CURRENT_BREAKPOINT = "SET_CURRENT_BREAKPOINT"
export const SET_SEO_FILTERS = "SET_SEO_FILTERS"
export const SET_MERCHANDISED_SEO_FILTERS = "SET_MERCHANDISED_SEO_FILTERS"
export const SET_BLOOMREACH_COOKIES_INIT_LOAD = "SET_BLOOMREACH_COOKIES_INIT_LOAD"
export const SET_COOKIE_CONSENT_VALUE = "SET_COOKIE_CONSENT_VALUE"
export const SET_FILTERS = "SET_FILTERS"
export const SET_IS_INITIAL_SCROLL_COMPLETED = "SET_IS_INITIAL_SCROLL_COMPLETED"
export const SET_FIRST_ELEMENT_ID = "SET_FIRST_ELEMENT_ID"
export const SET_ITEMS_CACHE = "SET_ITEMS_CACHE"
export const SET_ITEMS_REQUEST_PENDING = "SET_ITEMS_REQUEST_PENDING"
export const SET_ENABLE_CENTRE_TITLE = "SET_ENABLE_CENTRE_TITLE"
export const SET_NUMBER_OF_FACETS_LOADED = "SET_NUMBER_OF_FACETS_LOADED"
export const SET_FREE_TEXT_FILTER_SEARCH = "SET_FREE_TEXT_FILTER_SEARCH"
export const SET_SUBSIDISE_ITEMS_FOR_LOAD_MORE = "SET_SUBSIDISE_ITEMS_FOR_LOAD_MORE"
export const SET_MIN_PRICE = "SET_MIN_PRICE"
export const SET_MAX_PRICE = "SET_MAX_PRICE"
export const SET_SALE_CLEARANCE_SEARCH = "SET_SALE_CLEARANCE_SEARCH"

interface FixFacets {
    type: typeof FIX_FACETS
}

interface UnfixFacets {
    type: typeof UNFIX_FACETS
}

interface SetFacet {
    type: typeof SET_FACET
    value: string
    filterName: string
}

interface SetAllFacets {
    type: typeof SET_ALL_FACETS
    value: NewFiltersState
}

interface SetKeyFacets {
    type: typeof SET_KEY_FACETS
    value: NewFiltersState
}

export interface NewFiltersState {
    facets: FacetsState
}

export interface TabbedFiltersCTAState {
    facets: FacetsState
    searchFilters: Filters
}

interface SetClearTypeFilters {
    type: typeof SET_CLEAR_TYPE_FILTER
    payload: string
}
interface SetClearAllFilters {
    type: typeof SET_CLEAR_ALL_FILTER
}
interface SetClearPriceFilter {
    type: typeof SET_CLEAR_PRICE_FILTER
}
interface SetSearchData {
    type: typeof SET_SEARCH_DATA
    data: SearchState
}

interface SetSEOFilters {
    type: typeof SET_SEO_FILTERS
    data: string
}
interface SetCurrentBreakpoint {
    type: typeof SET_CURRENT_BREAKPOINT
    payload: keyof Breakpoints
}

interface SetInitialPageRangeFromPage {
    type: typeof SET_INITIAL_PAGE_RANGE_FROM_PAGE
    page: number
}

interface SetFilterIsOpenData {
    isOpen: boolean
    isViewMoreOpen: boolean
    name: string
}
interface SetFilterIsOpen {
    type: typeof SET_FILTER_IS_OPEN
    data: SetFilterIsOpenData
}
interface SetSelectedSorting {
    type: typeof SET_SELECTED_SORTING
    value: string
}

interface NextPageItemsReady {
    type: typeof NEXT_PAGE_ITEMS_READY
    items: ProductItem[]
    currentPage: number
}

interface SetPageItems {
    type: typeof SET_PAGE_ITEMS
    items: ProductItem[]
}

interface SetNewPageItems {
    type: typeof SET_NEW_PAGE_ITEMS
    items: ProductItem[]
}

interface SetIsFetchingPageItems {
    type: typeof SET_IS_FETCHING_PAGE_ITEMS
    value: boolean
}

interface SetFirstElementId {
    type: typeof SET_FIRST_ELEMENT_ID
    value: string
}

interface PreviousPageItemsReady {
    type: typeof PREVIOUS_PAGE_ITEMS_READY
    items: ProductItem[]
    currentPage: number
}

interface SetDebounceTime {
    type: typeof SET_DEBOUNCE_TIME
    time
}

interface SetInjectedAdsEnabled {
    type: typeof SET_INJECTED_ADS_ENABLED
    injectedAdsConfig
}

interface SetInjectedAdsHiddenForTimeout {
    type: typeof SET_INJECTED_ADS_HIDDEN_FOR_TIMEOUT
    injectedAdHiddenForTimeout
}

interface SetHideFooterUntilLastPageEnabled {
    type: typeof SET_HIDE_FOOTER_UNTIL_LAST_PAGE
    enabled: boolean
}

interface SetEnableStaticEsiEndpoint {
    type: typeof SET_ENABLE_STATIC_ESI_ENDPOINT
    enabled: boolean
}

interface AssignSearchState {
    type: typeof ASSIGN_SEARCH_STATE
    state: Partial<SearchState>
}

interface SetIsFetchingNextPage {
    type: typeof SET_IS_FETCHING_NEXT_PAGE
    value: boolean
}

interface SetIsFetchingPreviousPage {
    type: typeof SET_IS_FETCHING_PREVIOUS_PAGE
    value: boolean
}

interface SetIsInitialScrollCompleted {
    type: typeof SET_IS_INITIAL_SCROLL_COMPLETED
    value: boolean
}

interface SetSelectedPriceFilterData {
    facetName: string
    selectedMin: number
    selectedMax: number
}

interface SetSelectedPriceFilter {
    type: typeof SET_SELECTED_PRICE_FILTER
    data: SetSelectedPriceFilterData
}

interface SetBloomreachCookiesInitialLoadData {
    brUid2: string
    brMtSearch: string
    brSegId: string
}

interface SetBloomreachCookiesInitialLoad {
    type: typeof SET_BLOOMREACH_COOKIES_INIT_LOAD
    data: SetBloomreachCookiesInitialLoadData
}

interface SetCookieConsentValue {
    type: typeof SET_COOKIE_CONSENT_VALUE
    value: ConsentCookie
}

interface SetSingleOptionFacetList {
    type: typeof SET_SINGLE_OPTION_FACET_LIST
    data: string[]
}

interface SetSingleFacetFiltersToBePreservedInTheUrl {
    type: typeof SET_SINGLE_FACET_FILTERS_TO_BE_PRESERVED_IN_THE_URL
    data: string[]
}

interface SetFilters {
    type: typeof SET_FILTERS
    payload: Filters
}

interface SetItemsCache {
    type: typeof SET_ITEMS_CACHE
    items: {[key: string]: ProductItem[]}
}

interface SetItemsRequestPending {
    type: typeof SET_ITEMS_REQUEST_PENDING
    value: boolean
}

interface SetNumberOfFacetsLoaded {
    type: typeof SET_NUMBER_OF_FACETS_LOADED
    filterName: string
}

interface SetFreeTextFilterSearch {
    type: typeof SET_FREE_TEXT_FILTER_SEARCH
    value: {filterName: string; value: string}
}

interface SetSubsidiseItemsForLoadMore {
    type: typeof SET_SUBSIDISE_ITEMS_FOR_LOAD_MORE
    value: boolean
}

interface SetMinPrice {
    type: typeof SET_MIN_PRICE
    value: number
}
interface SetMaxPrice {
    type: typeof SET_MAX_PRICE
    value: number
}

interface SetSaleClearanceSearch {
    type: typeof SET_SALE_CLEARANCE_SEARCH
    term: string
}

type GetStateFn = () => State

export const initialState: SearchState = {
    totalResults: 0,
    freeTextFiltersList: [],
    singleOptionFacetList: [],
    singleFacetFiltersToBePreservedInTheUrl: [],
    filters: {},
    filtersSort: [],
    facets: {},
    items: [],
    itemsCache: {},
    sorting: {
        selected: "",
        options: [],
    },
    startPage: 1,
    endPage: 1,
    isFetchingNextPage: false,
    isFetchingPreviousPage: false,
    isFetchingPageItems: false,
    debounceTime: 700,
    itemsRequestPending: false,
    shouldFacetsBeFixed: false,
    autoCorrectQuery: null,
    title: "",
    initialScrollCompleted: false,
    firstElementId: "",
    itemsCacheSize: 60,
    pageSizeLimit: 900,
    rememberFilters: true,
    itemsPerPage: {
        initial: {
            mobile: 8,
            tablet: 12,
            desktop: 24,
        },
        subsequent: {
            mobile: 10,
            tablet: 16,
            desktop: 28,
        },
    },
    itemsPerRow: {
        xs: 0,
        sm: 0,
        md: 0,
        lg: 0,
        xl: 0,
        xxl: 0,
    },
    initialItemsPerPage: 0,
    fetchTriggerOffset: {
        xs: 0,
        sm: 0,
        md: 0,
        lg: 0,
        xl: 0,
        xxl: 0,
    },
    fetchTriggerOffsetRows: {
        xs: 0,
        sm: 0,
        md: 0,
        lg: 0,
        xl: 0,
        xxl: 0,
    },
    subsequentPagesNonLazyloadRows: {
        xs: 0,
        sm: 0,
        md: 0,
        lg: 0,
        xl: 0,
        xxl: 0,
    },
    relaxedQuery: "",
    currentBreakpoint: "lg",
    includedComponents: [],
    templateOverride: {},
    searchCategory: {
        id: "",
        name: "",
    },
    searchBannerHtml: null,
    searchBannerJson: null,
    seoFilters: "",
    bloomreachCookiesInitialVal: {
        brUid2: "",
        brMtSearch: "",
        brSegId: "",
    },
    searchProviderRequestUrl: "",
    injectedAdsConfig: {
        enabled: false,
        categorySearchAds: true,
        keywordSearchAds: false,
        adDetails: {
            position: 0,
        },
        environmentKeys: {
            sx: "dev",
            ci: "uat",
            pp: "conv",
            pd: "live",
        },
    },
    injectedAdHiddenForTimeout: false,
    consentCookieValue: {
        version: "0",
        consentBoxHasClosed: false,
        groups: [],
    },
    hideFooterUntilLastPage: false,
    enableStaticEsiEndpoint: false,
    productLoadTrigger: INFINITE_SCROLL,
    enableFonts: false,
    enableCentreTitle: false,
    disableIcons: {
        mobileFilter: false,
        mobileSort: false,
        moreLess: false,
    },
    numberOfFacetsLoaded: {},
    lazyLoadFilterFacetsInHorizontalFilters: [],
    alphabticallySortedFacetsInHorizontalFilters: [],
    showRealmInTopFiveBrands: false,
    enableStructuredBrowsing: false,
    freeTextFilterSearch: {},
    breadcrumbs: {
        enabled: true,
        breadcrumbsIcons: {
            enableBackButton: true,
            enableHomeIcon: true,
        },
    },
    subsidiseItemsForLoadMore: true,
    loadMoreClickCount: 0,
    breadcrumbsValues: [],
    structuredBrowsingChipsToLoadLimit: 10,
    priceSelectorIncrement: 150,
    apiProxyVersion: "v1",
    enableTaxonomy: false,
    redirectOldUrlsToNoResults: false,
    searchCategoryThumbnails: [],
    showCategoryThumbnails: false,
    compositeFiltersList: [],
    enableSearchBannerTemplating: false,
    minPrice: 0,
    maxPrice: 0,
    bloomreachDefaultProviderArgs: "_br_var_5",
    useProductAggregator: false,
    addNoIndexToCategoryFilterUrls: false,
    globalPriceFormattingConfig: {
        currencySymbol: "£",
        decimalSeparatorSymbol: ".",
        groupSeparatorSymbol: ",",
        position: "left",
        showNumOfDecimalDigits: 2,
        spaceRequired: false,
        textDirection: "ltr",
    } as CurrencyConfiguration,
    edgeAggregator: false,
    saleClearanceSearchTerm: "",
    adsConfig: {
        enabled: false,
        ads: [],
    },
    adsVersion: "",
    searchSource: "",
    advertsContent: [],
    themeName: "",
    containerMaxWidth: {
        fixedMaxWidth: "xl",
        customMaxWidth: null,
    },
}

export const selectItems = (state: State) => {
    return state.search.items
}

export function selectShouldFacetsBeFixed(state: State) {
    return state.search.shouldFacetsBeFixed
}

export const selectTotalPages = (state: State) => {
    const {totalResults} = state.search
    const subsequentItemsPerPage = selectSubsequentItemsPerPage(state)
    const subsequentPages = Math.ceil(totalResults / subsequentItemsPerPage)
    return subsequentPages
}

export const selectHasNextPage = (state: State) => {
    const {
        endPage,
        itemsPerPage: {initial, subsequent},
        subsidiseItemsForLoadMore,
        currentBreakpoint,
    } = state.search
    const itemsLoaded =
        (((endPage - 1) * subsequent[breakpointToViewportSize(currentBreakpoint)]) as number) +
        (subsidiseItemsForLoadMore ? (initial[breakpointToViewportSize(currentBreakpoint)] as number) : 0)
    return itemsLoaded < state.search.totalResults
}

export const selectHasPreviousPage = (state: State) => {
    return state.search.startPage > 1
}

export function selectIsFetchingNextPage(state: State) {
    return state.search.isFetchingNextPage
}

export function getCurrentBreakpoint(state: State) {
    return state.search.currentBreakpoint
}

export const fixFacets = (): FixFacets => {
    return {
        type: FIX_FACETS,
    }
}

export const unfixFacets = (): UnfixFacets => {
    return {
        type: UNFIX_FACETS,
    }
}

export const setSearchSelectedPriceFilter = (data: SetSelectedPriceFilterData): SetSelectedPriceFilter => {
    return {
        type: SET_SELECTED_PRICE_FILTER,
        data,
    }
}

export const setIsFetchingNextPageAction = (value: boolean): SetIsFetchingNextPage => {
    return {
        type: SET_IS_FETCHING_NEXT_PAGE,
        value,
    }
}

export const setIsFetchingPreviousPageAction = (value: boolean): SetIsFetchingPreviousPage => {
    return {
        type: SET_IS_FETCHING_PREVIOUS_PAGE,
        value,
    }
}

export const setFirstElementId = (value: string): SetFirstElementId => {
    return {
        type: SET_FIRST_ELEMENT_ID,
        value,
    }
}

export const setInitialScrollCompleted = (value: boolean): SetIsInitialScrollCompleted => {
    return {
        type: SET_IS_INITIAL_SCROLL_COMPLETED,
        value,
    }
}

export const assignSearchStateAction = (state: Partial<SearchState>): AssignSearchState => {
    return {
        type: ASSIGN_SEARCH_STATE,
        state,
    }
}

export const setCurrentBreakpoint = (payload: keyof Breakpoints): SetCurrentBreakpoint => {
    return {
        type: SET_CURRENT_BREAKPOINT,
        payload,
    }
}

export const setDebounceTimeAction = (time: number): SetDebounceTime => {
    return {
        type: SET_DEBOUNCE_TIME,
        time,
    }
}

type InjectedAdsConfigType = {
    enabled: boolean
    categorySearchAds: boolean
    keywordSearchAds: boolean
    adDetails: any
    environmentKeys: {
        sx: string
        ci: string
        pp: string
        pd: string
    }
}

export const setInjectedAdsConfigAction = (injectedAdsConfig: InjectedAdsConfigType): SetInjectedAdsEnabled => {
    return {
        type: SET_INJECTED_ADS_ENABLED,
        injectedAdsConfig,
    }
}
export const setInjectedAdsHiddenForTimeoutAction = (
    injectedAdHiddenForTimeout: boolean,
): SetInjectedAdsHiddenForTimeout => {
    return {
        type: SET_INJECTED_ADS_HIDDEN_FOR_TIMEOUT,
        injectedAdHiddenForTimeout,
    }
}

export const setHideFooterUntilLastPageAction = (enabled: boolean): SetHideFooterUntilLastPageEnabled => {
    return {
        type: SET_HIDE_FOOTER_UNTIL_LAST_PAGE,
        enabled,
    }
}

export const setEnableStaticEsiEndpoint = (enabled: boolean): SetEnableStaticEsiEndpoint => {
    return {
        type: SET_ENABLE_STATIC_ESI_ENDPOINT,
        enabled,
    }
}

export const setSingleOptionFacetList = (data: string[]): SetSingleOptionFacetList => {
    return {
        type: SET_SINGLE_OPTION_FACET_LIST,
        data,
    }
}

export const setSingleFacetFiltersToBePreservedInTheUrl = (
    data: string[],
): SetSingleFacetFiltersToBePreservedInTheUrl => {
    return {
        type: SET_SINGLE_FACET_FILTERS_TO_BE_PRESERVED_IN_THE_URL,
        data,
    }
}

export const nextPageItemsReadyAction = (items: ProductItem[], currentPage: number): NextPageItemsReady => {
    return {
        type: NEXT_PAGE_ITEMS_READY,
        items,
        currentPage,
    }
}

export const setPageItemsAction = (items: ProductItem[]): SetPageItems => {
    return {
        type: SET_PAGE_ITEMS,
        items,
    }
}

export const setNewPageItemsAction = (items: ProductItem[]): SetNewPageItems => {
    return {
        type: SET_NEW_PAGE_ITEMS,
        items,
    }
}

export const setIsFetchingPageItems = (value: boolean): SetIsFetchingPageItems => {
    return {
        type: SET_IS_FETCHING_PAGE_ITEMS,
        value,
    }
}

export const setItemsRequestPending = (value: boolean): SetItemsRequestPending => {
    return {
        type: SET_ITEMS_REQUEST_PENDING,
        value,
    }
}

export const setNumberOfFacetsLoaded = (filterName: string): SetNumberOfFacetsLoaded => {
    return {
        type: SET_NUMBER_OF_FACETS_LOADED,
        filterName,
    }
}

export const previousPageItemsReadyAction = (items: ProductItem[], currentPage: number): PreviousPageItemsReady => {
    return {
        type: PREVIOUS_PAGE_ITEMS_READY,
        items,
        currentPage,
    }
}

export const setFiltersAction = (filters: Filters): SetFilters => {
    return {
        type: SET_FILTERS,
        payload: filters,
    }
}

export const setCurrentBreakpointAction = (breakpoint: keyof Breakpoints) => (dispatch: Dispatch<Action>) => {
    dispatch(setCurrentBreakpoint(breakpoint))
}

export const setItemsCache = (items: {[key: string]: ProductItem[]}): SetItemsCache => {
    return {
        type: SET_ITEMS_CACHE,
        items,
    }
}

export const fetchSearchCategoryThumbnailsThunk = () => async (dispatch: Dispatch, getState: GetStateFn) => {
    const state = getState()
    if (state.search.showCategoryThumbnails) {
        try {
            const settingsFileNamePath = "/configs/searchCategoryThumbnails/searchCategoryThumbnails.settings.json"
            const CDNPath = env.REACT_APP_BLOB_STORAGE_PATH

            const {data} = await axios(`${CDNPath}${settingsFileNamePath}`)
            dispatch<Action>(assignSearchStateAction({searchCategoryThumbnails: data.searchCategoryThumbnails}))
        } catch (error) {
            logger.error(error, "fetchSearchCategoryThumbnailsThunk")
            dispatch<Action>(assignSearchStateAction({searchCategoryThumbnails: []}))
        }
    }
}

export const setPageItemsThunk =
    ({
        historyUrl,
        enableDebounce,
        isFromSortDropdown,
    }: {historyUrl?: string; enableDebounce?: boolean; isFromSortDropdown?: boolean} = {}) =>
    async (dispatch: Dispatch, getState: GetStateFn) => {
        let cancelToken
        try {
            logger.debug("Fetching filtered items")
            dispatch<Action>(assignSearchStateAction({subsidiseItemsForLoadMore: true, loadMoreClickCount: 0}))
            dispatch<Action>(setIsFetchingPageItems(true))
            const state = getState()
            const {headers, searchTerm, type, siteUrl, bloomreachPersonalizationEnabled} = state.request
            const {
                debounceTime,
                currentBreakpoint,
                itemsPerPage,
                bloomreachCookiesInitialVal,
                initialItemsPerPage,
                itemsCacheSize,
                rememberFilters,
                apiProxyVersion,
                enableSearchBannerTemplating,
                templateOverride = {},
                bloomreachDefaultProviderArgs,
            } = state.search
            const {enableNewProductSummary} = state.features
            if (enableDebounce) {
                cancelToken = await debounceAndGetCancelToken(debounceTime)
            }
            dispatch<Action>(setItemsRequestPending(true))
            const url =
                historyUrl ??
                build(
                    state.request.url,
                    !isFromSortDropdown && state.search.sorting?.selected === "score"
                        ? ""
                        : state.search.sorting?.selected,
                    state.request.type,
                    state.request.isMerchandisedUrl,
                    state.search.facets,
                    state.search.filters?.Price as FilterPriceApiResponse,
                    state.search.singleFacetFiltersToBePreservedInTheUrl,
                )
            const win = getWindow()
            if (!historyUrl && win) win.history.pushState({url, type: HISTORY_EVENT}, "", url)
            const fields: (keyof ExternalSearchApiResponse)[] = [
                "items",
                "filters",
                "totalResults",
                "sorting",
                "title",
                "relaxedQuery",
                "includedComponents",
                "searchCategory",
                "templateOverride",
            ]
            // if SearchBannerTemplating is enabled then add searchBanner to fields
            if (enableSearchBannerTemplating) {
                fields.push("searchBanner")
            }
            const {startPage, endPage, pageSize, scrollLocation} = getInitialPage(
                itemsPerPage,
                currentBreakpoint,
                historyUrl,
            )
            const pageLoadTrigger: pageLoadTriggerType = "FILTER"
            let getFragments
            if (enableNewProductSummary) getFragments = getSearchResults
            else getFragments = getProductsFragmentByPage
            const {productSummaryTemplateName, imageRatio} = templateOverride
            const apiResponse = await getFragments({
                url,
                baseUrl: urlSanitiser(siteUrl),
                headers,
                searchTerm,
                type,
                pageSize: itemsCacheSize,
                fields,
                cancelToken,
                bloomreachCookiesInitialVal,
                bloomreachPersonalizationEnabled,
                pageLoadTrigger,
                sliceSize: initialItemsPerPage,
                start: 0,
                apiProxyVersion,
                productSummaryTemplateName,
                imageRatio,
                bloomreachDefaultProviderArgs,
            })
            const lazyloadItemIndexFrom = getLazyloadItemIndexFrom(state)
            const {
                search: previousSearchState,
                tabbedFilters: {isOpen: isTabbedFiltersOpen},
            } = getState()
            let decodedApiResponse: MapProductsFragmentToItemsResponse | MapProductsFragmentJsonToItemsResponse
            if (enableNewProductSummary) decodedApiResponse = mapProductsFragmentJsonToItems(apiResponse)
            else decodedApiResponse = mapProductsFragmentToItems(apiResponse, lazyloadItemIndexFrom, FILTERING)
            let items = []
            if (apiResponse.split(ESISeparator.ProductArray).length === 2) {
                const itemsArrayWithTranslationTags = apiResponse.split(ESISeparator.ProductArray)[1]
                const itemsArrayString = removeScriptTags(itemsArrayWithTranslationTags)
                items = JSON.parse(itemsArrayString)
            }
            const searchState = formatSearchState(
                previousSearchState,
                decodedApiResponse,
                items,
                initialItemsPerPage,
                itemsCacheSize,
            )
            const cookie = Cookies.getJSON(FILTER_SETTINGS_COOKIE) as FilterCookie
            const newCookieState = mapApiDataToState({
                data: searchState,
                type,
                isServerSideRender: false,
                cacheStart: 0,
                rememberFilters,
                cookie,
                existingFilters: previousSearchState.filters,
                sliceStart: 0,
                sliceEnd: selectInitialItemsPerPage(getState() as State),
                initialLoad: true,
                initialItemsPerPage: selectInitialItemsPerPage(getState() as State),
                subsequentItemsPerPage: selectSubsequentItemsPerPage(getState() as State),
            })
            const subsequentItemsPerPage = selectSubsequentItemsPerPage(state)
            const ids = newCookieState.items.map(({itemNumber}, index) => ({
                itemNumber,
                index: (startPage - 1) * subsequentItemsPerPage + index,
            }))
            if (enableNewProductSummary) {
                updateProductSummaryItemsData(
                    decodedApiResponse.items as MapProductsFragmentJsonToItemsResponseItems[],
                    {
                        dispatch,
                        getState,
                    },
                )
            }
            PublishProductSummaryTrackPage(ids)
            pushSearchResultsEvent(state)
            dispatch<Action>(setSearchDataAction(newCookieState))
            dispatch<Action>(
                assignSearchStateAction({
                    startPage,
                    endPage,
                    initialItemsPerPage: pageSize,
                }),
            )
            dispatch<Action>(assignRequestDuck({url}))
            const stateUpdatedUrl: State = {...state, request: {...state.request, url}}
            const seoMetadata = selectSeoMetadata(stateUpdatedUrl)
            if (type !== SearchApiRequestTypes.Keyword) {
                seoMetadata.canonical = url
            }
            updateDocumentSeoMetadata(seoMetadata)
            window.scrollTo(0, scrollLocation)
            // Show the facets list
            // window.requestIdleCallback(() => {
            dispatch<Action>(fixFacets())
            dispatch<Action>(unfixFacets())
            // })
            if (isTabbedFiltersOpen) {
                dispatch(syncTabbedFiltersFromSearch(true) as any)
            }
        } catch (error) {
            const err = error as Error
            const state = getState()
            const {siteUrl} = state.request
            if (err.message === TOKEN_CANCELLATION_FLAG) return

            dispatch<Action>(setPageItemsAction([]))
            scrollToTop()
            window.location.href = `${redirectErrorPage(siteUrl)}`
            logger.error(err, "set-page-items-error")
            throw err
        }
    }

export const getProductsFragmentFromItemsCache = async (
    {
        headers,
        url,
        searchTerm,
        type,
        start: startingItem,
        bloomreachCookiesInitialVal,
        bloomreachPersonalizationEnabled,
        fields,
    },
    state: State,
    dispatch: Dispatch,
) => {
    // if we already have the requested items on our itemsCache
    // send a request (getProductFragmentsById) to get the fragments for each product
    // if the items are not in cache
    // send a request (getProductsFragment) to receive both fragments and additional items for the cache

    try {
        const {
            itemsCache,
            itemsCacheSize,
            enableStaticEsiEndpoint,
            templateOverride = {},
            apiProxyVersion,
            bloomreachDefaultProviderArgs,
        } = state.search
        const {siteUrl} = state.request
        const {enableNewProductSummary} = state.features
        const {productSummaryTemplateName, imageRatio} = templateOverride
        if (itemsCache[startingItem]) {
            let itemsArray = state.search.itemsCache[startingItem]
            itemsArray = getItemsStringWithoutHTML(itemsArray)

            let itemsString = ""
            if (enableStaticEsiEndpoint || enableNewProductSummary) {
                itemsString = generateItemsString(itemsArray, templateOverride)
            } else {
                // removing brackets as Motionpoint errors when there is a square bracket in the request
                itemsString = JSON.stringify(
                    mapItems({
                        items: itemsArray,
                        imageRatio,
                        productSummaryTemplateName,
                    }),
                )
                itemsString = itemsString.slice(1, itemsString.length - 1)
            }

            let getFragments = getProductFragmentsById
            if (enableStaticEsiEndpoint) getFragments = getStaticProductFragmentsById
            else if (enableNewProductSummary) getFragments = getProductSummaryAggregation

            const productFragments = await getFragments(headers, itemsString, apiProxyVersion, siteUrl, url)
            return productFragments
        }

        const smallestItemSequence = parseInt(Object.keys(itemsCache)[0], 10)
        const pageLoadTrigger: pageLoadTriggerType = startingItem < smallestItemSequence ? "PREVIOUS" : "NEXT"

        let itemsToLoad = itemsCacheSize
        let newStartingItem = startingItem

        if (pageLoadTrigger === "PREVIOUS") {
            if (startingItem - itemsCacheSize < 0) {
                itemsToLoad = smallestItemSequence
                newStartingItem = 0
            } else {
                newStartingItem = smallestItemSequence - itemsCacheSize
            }
        }

        let getFragments
        if (enableNewProductSummary) getFragments = getSearchResults
        else getFragments = getProductsFragment

        const fragment = await getFragments({
            headers,
            url,
            baseUrl: siteUrl,
            searchTerm,
            type,
            start: newStartingItem,
            pageSize: itemsToLoad,
            bloomreachCookiesInitialVal,
            bloomreachPersonalizationEnabled,
            fields,
            pageLoadTrigger,
            sliceSize: selectSubsequentItemsPerPage(state),
            apiProxyVersion,
            productSummaryTemplateName,
            imageRatio,
            bloomreachDefaultProviderArgs,
        })

        if (fragment.split(ESISeparator.ProductArray).length === 2) {
            const itemsArrayWithTranslationTags = fragment.split(ESISeparator.ProductArray)[1]
            const itemsArrayString = removeScriptTags(itemsArrayWithTranslationTags)
            const items = JSON.parse(itemsArrayString)

            addItemsToCache(
                items,
                state,
                dispatch,
                newStartingItem,
                false,
                selectSubsequentItemsPerPage(state),
                false,
                selectInitialItemsPerPage(state),
                selectSubsequentItemsPerPage(state),
            )
            return fragment.split(ESISeparator.ProductArray)[0] || ""
        }
        return ""
    } catch (error) {
        const {siteUrl} = state.request
        dispatch<Action>(setIsFetchingPreviousPageAction(false))
        logger.error(error, "get-product-fragments-from-cache-error")
        window.location.href = `${redirectErrorPage(siteUrl)}`
        throw error
    }
}

export const addItemsToCache = (
    itemsToBeAdded: any[],
    state: State,
    dispatch: Dispatch,
    startFrom: number,
    clearExistingItems,
    pageSize: number,
    initialLoad: boolean,
    initialItemsPerPage: number,
    subsequentItemsPerPage: number,
) => {
    dispatch<Action>(
        setItemsCache({
            ...(clearExistingItems ? {} : state.search.itemsCache),
            ...createItemsCache({
                items: itemsToBeAdded,
                startFrom,
                initialLoad,
                initialItemsPerPage,
                subsequentItemsPerPage,
            }),
        }),
    )
}

export const fetchPreviousPageItemsThunk =
    (isLazyLoadEnabled = true) =>
    async (dispatch: Dispatch, getState: GetStateFn) => {
        try {
            const state = getState()
            const {startPage, bloomreachCookiesInitialVal} = state.search
            const subsequentItemsPerPage = selectSubsequentItemsPerPage(state)
            const pageToFetch = startPage - 1
            const {headers, url, searchTerm, type, bloomreachPersonalizationEnabled} = state.request
            const {enableNewProductSummary} = state.features
            logger.debug(`Fetching page ${pageToFetch} of items`)
            dispatch<Action>(setIsFetchingPreviousPageAction(true))
            const fields: (keyof ExternalSearchApiResponse)[] = ["items"]

            const fragment = await getProductsFragmentFromItemsCache(
                {
                    headers,
                    url,
                    searchTerm,
                    type,
                    start: (startPage - 2) * subsequentItemsPerPage,
                    bloomreachCookiesInitialVal,
                    bloomreachPersonalizationEnabled,
                    fields,
                },
                state,
                dispatch,
            )

            const numberOfItemsToLazyload = getLazyloadItemIndexFrom(state)
            let products: MapProductsFragmentToItemsResponse | MapProductsFragmentJsonToItemsResponse

            if (enableNewProductSummary) products = mapProductsFragmentJsonToItems(fragment || "")
            else
                products = mapProductsFragmentToItems(
                    fragment || "",
                    numberOfItemsToLazyload,
                    isLazyLoadEnabled ? PREV_PAGE : NO_LAZYLOAD,
                )

            const itemsPerPage = selectSubsequentItemsPerPage(state)
            const ids = products.items.map(({itemNumber}, index) => ({
                itemNumber,
                index: (pageToFetch - 1) * itemsPerPage + index,
            }))

            if (enableNewProductSummary) {
                updateProductSummaryItemsData(products.items as MapProductsFragmentJsonToItemsResponseItems[], {
                    dispatch,
                    getState,
                })
            }

            PublishProductSummaryTrackPage(ids)
            pushSearchResultsEvent(state, pageToFetch)
            const postFetchState = getState()
            // Check that the flag hasn't been reset by any other action. eg setPageItemsThunk
            if (postFetchState.search.isFetchingPreviousPage) {
                dispatch<Action>(previousPageItemsReadyAction(products.items, pageToFetch))
            }
        } catch (error) {
            const state = getState()
            const {siteUrl} = state.request
            dispatch<Action>(setIsFetchingPreviousPageAction(false))
            logger.error(error, "fetch-previous-products-page-error")
            window.location.href = `${redirectErrorPage(siteUrl)}`
            throw error
        }
    }
export const fetchNextPageItemsThunk = () => async (dispatch: Dispatch, getState: GetStateFn) => {
    try {
        const state = getState()
        const {endPage, bloomreachCookiesInitialVal, subsidiseItemsForLoadMore, loadMoreClickCount} = state.search
        const {bloomreachPersonalizationEnabled, headers, url, searchTerm, type} = state.request
        const {enableNewProductSummary} = state.features
        const subsequentItemsPerPage = selectSubsequentItemsPerPage(state)
        const initialItemsPerPage = selectInitialItemsPerPage(state)

        const pageToFetch = endPage + 1

        logger.debug(`Fetching page ${pageToFetch} of items`)
        dispatch<Action>(setIsFetchingNextPageAction(true))
        const fields: (keyof ExternalSearchApiResponse)[] = ["items"]
        let startingItem = endPage * subsequentItemsPerPage
        if (!initialAndSubsequentPageSizeEqual(state)) {
            startingItem = (endPage - 1) * subsequentItemsPerPage
        }
        if (subsidiseItemsForLoadMore && !initialAndSubsequentPageSizeEqual(state)) {
            startingItem = (endPage - 1) * subsequentItemsPerPage + initialItemsPerPage
        }
        if (loadMoreClickCount === 1 && !initialAndSubsequentPageSizeEqual(state)) {
            startingItem = (endPage - 1) * subsequentItemsPerPage
        }

        if (endPage >= MAX_PAGE_TO_LOAD) {
            dispatch<Action>(setIsFetchingNextPageAction(false))
            return
        }

        const fragment = await getProductsFragmentFromItemsCache(
            {
                headers,
                url,
                searchTerm,
                type,
                start: startingItem,
                bloomreachCookiesInitialVal,
                bloomreachPersonalizationEnabled,
                fields,
            },
            state,
            dispatch,
        )

        const numberOfItemsToLazyload = getLazyloadItemIndexFrom(state)
        let products: MapProductsFragmentToItemsResponse | MapProductsFragmentJsonToItemsResponse

        if (enableNewProductSummary) products = mapProductsFragmentJsonToItems(fragment || "")
        else products = mapProductsFragmentToItems(fragment || "", numberOfItemsToLazyload, NEXT_PAGE)

        // TODO: filter out empty tiles
        const ids = products.items.map(({itemNumber}, index) => ({itemNumber, index: startingItem + index}))
        if (enableNewProductSummary) {
            updateProductSummaryItemsData(products.items as MapProductsFragmentJsonToItemsResponseItems[], {
                dispatch,
                getState,
            })
        }
        PublishProductSummaryTrackPage(ids)
        const postFetchState = getState()
        pushSearchResultsEvent(postFetchState, pageToFetch)
        dispatch<Action>(
            assignSearchStateAction({
                loadMoreClickCount: loadMoreClickCount + 1,
                subsidiseItemsForLoadMore: false,
            }),
        )
        if (postFetchState.search.isFetchingNextPage) {
            // window.requestIdleCallback(() => {
            dispatch<Action>(nextPageItemsReadyAction(products.items, pageToFetch))
            // })
        }
    } catch (error) {
        const state = getState()
        const {siteUrl} = state.request
        dispatch<Action>(setIsFetchingNextPageAction(false))
        logger.error(error, "fetch-next-products-page-error")
        window.location.href = `${redirectErrorPage(siteUrl)}`
        throw error
    }
}

export const setFilterClearTypeAction = (type: string) => dispatch => {
    dispatch({
        type: SET_CLEAR_TYPE_FILTER,
        payload: type,
    })
    dispatch(setPageItemsThunk({enableDebounce: false}))
}

export const setFilterClearAllAction =
    (clearSort = false) =>
    dispatch => {
        dispatch({
            type: SET_CLEAR_ALL_FILTER,
        })
        dispatch({
            type: SET_CLEAR_PRICE_FILTER,
        })
        dispatch(setPageItemsThunk({enableDebounce: true, isFromSortDropdown: clearSort}))
    }

export const setFilterAndSortClearAllAction = () => (dispatch: any, getState: GetStateFn) => {
    const state = getState()
    const {selected} = state.search.sorting
    if (selected !== SCORE) {
        dispatch({
            type: SET_SELECTED_SORTING,
            value: SCORE,
        })
        dispatch(setFilterClearAllAction(true))
    } else {
        dispatch(setFilterClearAllAction())
    }
}

const setSearchDataAction = (data: SearchState): SetSearchData => {
    return {
        type: SET_SEARCH_DATA,
        data,
    }
}

const setSEOFiltersAction = (data: string): SetSEOFilters => {
    return {
        type: SET_SEO_FILTERS,
        data,
    }
}

export const setInitialPageRangeFromPageAction = (page: number): SetInitialPageRangeFromPage => {
    return {
        type: SET_INITIAL_PAGE_RANGE_FROM_PAGE,
        page,
    }
}

const setFacetsOpenAction = (isOpen: boolean, isViewMoreOpen: boolean, name: string): SetFilterIsOpen => {
    const data = {isOpen, isViewMoreOpen, name}

    return {
        type: SET_FILTER_IS_OPEN,
        data,
    }
}

export const setSelectedSortingAction = (value: string) => dispatch => {
    dispatch({
        type: SET_SELECTED_SORTING,
        value,
    })
    dispatch(setPageItemsThunk({isFromSortDropdown: true}))
}

export const setSelectedPriceFilterAction =
    (facetName: string, selectedMin: number, selectedMax: number) => dispatch => {
        dispatch({
            type: SET_SELECTED_PRICE_FILTER,
            data: {facetName, selectedMin, selectedMax},
        })
        dispatch(setPageItemsThunk())
    }

export const updateProducts = async (
    {dispatch, getState}: ReturnType<typeof makeStore>,
    validTemplates: TemplateSettingsDuckState,
    validImageRatios,
    req,
    cookie?: FilterCookie,
): Promise<GenericSearchResponse> => {
    try {
        const {
            search: {
                startPage,
                bloomreachCookiesInitialVal,
                itemsCacheSize,
                rememberFilters,
                enableTaxonomy,
                redirectOldUrlsToNoResults,
                enableSearchBannerTemplating,
                bloomreachDefaultProviderArgs,
                adsConfig,
                themeName,
            },
            request: {
                headers,
                url,
                searchTerm,
                type,
                bloomreachPersonalizationEnabled,
                siteUrl,
                isMerchandisedUrl,
                page,
            },
            seoFilters: {filterDepthCount, merchandisedURLFilterDepthCount},
            template,
            productSummaryTemplate,
            features: {enableNewProductSummary},
            productSummary: {imageRatio},
        } = getState() as State

        const fields: (keyof ExternalSearchApiResponse)[] = [
            "items",
            "filters",
            "totalResults",
            "sorting",
            "title",
            "autoCorrectQuery",
            "relaxedQuery",
            "includedComponents",
            "searchCategory",
            "searchProviderRequestUrl",
            "templateOverride",
        ]
        // if SearchBannerTemplating is enabled then add searchBanner to fields
        if (enableSearchBannerTemplating) {
            fields.push("searchBanner")
        }
        let response: GenericSearchResponse = new SearchApiResponse()
        // URLs should redirect to no results page if the following conditions are met
        // 1. Taxonomy country and old url
        const isTaxonomyCategoryURL =
            enableTaxonomy && type === SearchApiRequestTypes.Category && redirectOldUrlsToNoResults
        const isValidURL = isTaxonomyCategoryURL ? isMerchandisedUrl : true

        if (isValidURL) {
            response = await getProducts({
                headers,
                url,
                searchTerm,
                type,
                bloomreachCookiesInitialVal,
                bloomreachPersonalizationEnabled,
                sliceSize: 0,
                fields,
                start: (startPage - 1) * selectSubsequentItemsPerPage(getState() as State),
                pageSize: itemsCacheSize,
                bloomreachDefaultProviderArgs,
            })
            if (isRedirectResponse(response)) {
                return response
            }
        }

        extractTemplateOverride(
            response,
            template,
            validTemplates,
            "plpTemplateName",
            (mainTemplate, fallbackTemplate) =>
                dispatch({
                    type: "SET_TEMPLATE",
                    // @ts-ignore TODO: this is a temporary fix to override template by category so we can show the right template on NEW GEL Sofa pages
                    ...overrideTemplateByCategory(themeName, response.templateOverride?.plpTemplateName, {
                        mainTemplate,
                        fallbackTemplate,
                    }),
                }),
        )
        extractTemplateOverride(
            response,
            productSummaryTemplate,
            validTemplates,
            "productSummaryTemplateName",
            (mainTemplate, fallbackTemplate) => dispatch({type: "SET_PS_TEMPLATE", mainTemplate, fallbackTemplate}),
        )

        updateImageRatio(dispatch, extractImageRatio(response, validImageRatios, imageRatio))

        const state = mapApiDataToState({
            data: response,
            type,
            isServerSideRender: true,
            cacheStart: (startPage - 1) * selectSubsequentItemsPerPage(getState() as State),
            rememberFilters,
            cookie,
            sliceStart: 0,
            sliceEnd: selectInitialItemsPerPage(getState() as State),
            initialLoad: true,
            initialItemsPerPage: selectInitialItemsPerPage(getState() as State),
            subsequentItemsPerPage: selectSubsequentItemsPerPage(getState() as State),
        })

        if (!state.totalResults) {
            dispatch(populateCategoryQuickLinks())
        }

        let seoFilters = ""
        const areaSegment = getAreaSegment(url)
        if (areaSegment && Object.values(AreaSegmentOptions).includes(areaSegment)) {
            const urlSegments = getCategorySearchUrlSegment(
                url.substring(url.indexOf(`/${areaSegment}`)),
                SearchApiRequestTypes.Category,
            )
            if (isMerchandisedUrl) {
                const splitPathArray = url.substring(url.indexOf(`/${areaSegment}`)).split("/")
                if (splitPathArray.length > 2 && !url.endsWith("/shop/")) {
                    const filtersArray = getFiltersWithoutPriceAndSort(urlSegments.filter)

                    const filterDepth = filtersArray.length
                    const allowIndexOfFilters = indexMerchandiseURLFilters(
                        urlSegments.filter,
                        merchandisedURLFilterDepthCount,
                    )
                    if (
                        (urlSegments.filter === "" || filterDepth < merchandisedURLFilterDepthCount) &&
                        allowIndexOfFilters
                    ) {
                        seoFilters = getMerchandisedSEOFiltersHTML(
                            response,
                            url,
                            urlSegments,
                            siteUrl,
                            merchandisedURLFilterDepthCount,
                            getState() as State,
                        )
                        dispatch(setSEOFiltersAction(seoFilters))
                    }
                }
            } else {
                // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
                const filterDepth = urlSegments.filter.match(CATEGORY_FILTERS_REGEX)?.length || 0
                const allowIndexOfFilters = indexFilters(urlSegments.filter) && indexFilters(urlSegments.category)

                if ((urlSegments.filter === "" || filterDepth < filterDepthCount) && allowIndexOfFilters) {
                    seoFilters = getSEOFiltersHTML(response, url, urlSegments, siteUrl)
                    dispatch(setSEOFiltersAction(seoFilters))
                }
            }
        }

        let advertsContent: AdvertsContent[] = []

        // call adverts API
        if (adsConfig.enabled && page <= 1) {
            // Remove query params from the URL as it is not needed for the adverts API
            const baseUrl = req.originalUrl.replace(/\?.*$/, "")
            const urlPath = urlSanitiser(encodeURIComponent(baseUrl))
            const configResponse = await getAdsConfig({
                headers,
                urlPath,
            })
            advertsContent = configResponse.content ?? []
        }

        dispatch(setSearchDataAction({...state, advertsContent}))
        dispatch(updateSaleThunk(response.saleDisplayText) as any)

        if (enableNewProductSummary) await updateProductSummary({dispatch, getState})
        return response
    } catch (err) {
        const error = err as AxiosError
        if (!error.response) {
            logger.error(error)
        }
        throw error
    }
}

export const updateFiltersOpenFromCookie =
    (filterCookie: FilterCookie) => (dispatch: Dispatch, getState: GetStateFn) => {
        const {
            search: {filters},
            request: {url, siteUrl, type, searchTerm},
        } = getState()

        const category = getCategorySegmentFromUrl(url, siteUrl, type)
        const cookieKey = type === SearchApiRequestTypes.Category ? category : searchTerm

        if (!filterCookie || filterCookie.page !== cookieKey) {
            removeCookieFromCurrentDomain(FILTER_SETTINGS_COOKIE, url)
            return
        }

        let hasUpdatedState = false
        Object.entries(filterCookie.filterCategorySettings).forEach(([key, value]) => {
            if (key in filters) {
                const filter = filters[key]
                if (isPriceFilter(filter)) {
                    if (filter.isFilterOpen !== value.isOpen) {
                        hasUpdatedState = true
                    }
                    filter.isFilterOpen = value.isOpen
                } else {
                    if (filter.isFilterOpen !== value.isOpen || filter.isViewMoreOpen !== value.viewMoreOpened) {
                        hasUpdatedState = true
                    }
                    filter.isFilterOpen = value.isOpen
                    filter.isViewMoreOpen = value.viewMoreOpened
                }
            }
        })
        if (hasUpdatedState) {
            // window.requestIdleCallback(() => {
            dispatch<Action>(setFiltersAction(filters))
            // })
        }
    }

export const updateFiltersOpenFromFacets = () => (dispatch: Dispatch, getState: GetStateFn) => {
    const {
        search: {filters, facets},
    } = getState()
    const newFilters = getOpenFiltersFromFacets(facets, filters)
    // window.requestIdleCallback(() => {
    dispatch<Action>(setFiltersAction(newFilters))
    // })
}

export const setFilterIsOpenThunk = (isOpen: boolean, name: string) => (dispatch: Dispatch, getState: GetStateFn) => {
    const {
        request: {url, searchTerm, type, siteUrl},
        search: {filters, rememberFilters},
    } = getState()

    const filter = filters[name]
    if (isPriceFilter(filter)) {
        if (rememberFilters) setFilterCookie(searchTerm, type, url, siteUrl, name, isOpen, false)
        dispatch<Action>(setFacetsOpenAction(isOpen, false, name))
    } else {
        if (rememberFilters) setFilterCookie(searchTerm, type, url, siteUrl, name, isOpen, filter.isViewMoreOpen)
        dispatch<Action>(setFacetsOpenAction(isOpen, filter.isViewMoreOpen, name))
    }
}

export const setViewMoreIsOpenThunk =
    (isViewMoreOpen: boolean, name: string) => (dispatch: Dispatch, getState: GetStateFn) => {
        const {
            request: {url, searchTerm, type, siteUrl},
            search: {filters, rememberFilters},
        } = getState()

        const filter = filters[name]
        if (rememberFilters) setFilterCookie(searchTerm, type, url, siteUrl, name, filter.isFilterOpen, isViewMoreOpen)
        dispatch<Action>(setFacetsOpenAction(filter.isFilterOpen, isViewMoreOpen, name))
    }

export const setFacet =
    (value: string, enableDebounce = true) =>
    dispatch => {
        dispatch({
            type: SET_FACET,
            value,
        })
        dispatch(setPageItemsThunk({enableDebounce}))
    }

export const setFreeTextFilterSearch = (value: {filterName: string; value: string}): SetFreeTextFilterSearch => {
    return {
        type: SET_FREE_TEXT_FILTER_SEARCH,
        value,
    }
}
export const setSubsidiseItemsForLoadMore = (value: boolean): SetSubsidiseItemsForLoadMore => {
    return {
        type: SET_SUBSIDISE_ITEMS_FOR_LOAD_MORE,
        value,
    }
}
export const toggleViewMoreAction = (facetName: string, filterName: string) => (dispatch, getState) => {
    const state = getState()
    const {isViewMoreOpen} = state.search.filters[facetName]
    dispatch(setViewMoreIsOpenThunk(!isViewMoreOpen, facetName))
    if (isViewMoreOpen) {
        TrackIsViewLessTriggeredFilter(filterName)
    } else {
        TrackIsViewMoreTriggeredFilter(filterName)
    }
}

export const toggleAccordionAction =
    ({event, name, displayName}: {event: SyntheticEvent<Element, Event>; name: string; displayName: string}) =>
    (dispatch, getState) => {
        event.preventDefault()
        const state = getState()
        const filter = state.search.filters[name]

        if (!filter.isFilterOpen) {
            TrackFilterExpanded(displayName)
        }
        dispatch(setFilterIsOpenThunk(!filter.isFilterOpen, name))
    }

export function mapToInitialPageRange(value: number): PageRange {
    let endPage = 1
    let startPage = 1

    if (value > 1) {
        endPage = value
        startPage = value
    }

    if (value >= MAX_PAGE_TO_LOAD) {
        endPage = 1
        startPage = 1
    }

    return {startPage, endPage}
}

export const setAllFacets = (value: TabbedFiltersCTAState) => dispatch => {
    dispatch({
        type: SET_ALL_FACETS,
        value,
    })

    TrackCTATabbedFilters(selectedFacetFilterTitle(value))

    return dispatch(setPageItemsThunk()) as Dispatch
}

export const setKeyFacets = (value: NewFiltersState, keyFacetName: string, keyFacetSelected: boolean) => dispatch => {
    dispatch({
        type: SET_KEY_FACETS,
        value,
    })

    if (keyFacetSelected) {
        TrackFacetSelection("feat", keyFacetName)
    } else {
        TrackFacetDeselection("feat", keyFacetName)
    }

    return dispatch(setPageItemsThunk()) as Dispatch
}

export const applyAllFacets = () => (dispatch: Dispatch, getState: GetStateFn) => {
    const {
        viewAllModal: {facets},
        search,
    } = getState()
    const newFacets: FacetsState = Object.entries(facets).reduce((actionData, [filterName, facetValue]) => {
        // eslint-disable-next-line no-param-reassign
        actionData[filterName] = {...facetValue}
        return actionData
    }, {} as FacetsState)
    dispatch<Action>(setViewAllCloseAction())
    setAllFacets({facets: newFacets, searchFilters: search.filters})(dispatch)
}

export const setBloomreachCookiesInitialLoad = (
    data: SetBloomreachCookiesInitialLoadData,
): SetBloomreachCookiesInitialLoad => {
    return {
        type: SET_BLOOMREACH_COOKIES_INIT_LOAD,
        data,
    }
}

export const setCookieConsentValue = (value: ConsentCookie): SetCookieConsentValue => {
    return {
        type: SET_COOKIE_CONSENT_VALUE,
        value,
    }
}

export const setSaleClearanceSearchTerm = (term: string): SetSaleClearanceSearch => ({
    type: SET_SALE_CLEARANCE_SEARCH,
    term,
})

const reducer = (
    state: SearchState = initialState,
    action:
        | SetSearchData
        | SetSelectedSorting
        | SetFilterIsOpen
        | NextPageItemsReady
        | PreviousPageItemsReady
        | SetPageItems
        | SetNewPageItems
        | SetIsFetchingPageItems
        | SetIsFetchingNextPage
        | SetIsFetchingPreviousPage
        | SetItemsRequestPending
        | SetFacet
        | SetSelectedPriceFilter
        | SetInitialPageRangeFromPage
        | SetAllFacets
        | SetKeyFacets
        | SetDebounceTime
        | FixFacets
        | UnfixFacets
        | SetClearAllFilters
        | SetClearTypeFilters
        | SetClearPriceFilter
        | AssignSearchState
        | SetCurrentBreakpoint
        | SetSEOFilters
        | SetBloomreachCookiesInitialLoad
        | SetSingleOptionFacetList
        | SetSingleFacetFiltersToBePreservedInTheUrl
        | SetFilters
        | SetInjectedAdsEnabled
        | SetInjectedAdsHiddenForTimeout
        | SetIsInitialScrollCompleted
        | SetFirstElementId
        | SetItemsCache
        | SetCookieConsentValue
        | SetHideFooterUntilLastPageEnabled
        | SetEnableStaticEsiEndpoint
        | SetNumberOfFacetsLoaded
        | SetFreeTextFilterSearch
        | SetSubsidiseItemsForLoadMore
        | SetMinPrice
        | SetMaxPrice
        | SetSaleClearanceSearch,
): SearchState => {
    switch (action.type) {
        case SET_FACET: {
            const newState: SearchState = {
                ...state,
                facets: {
                    ...state.facets,
                    [action.value]: {...state.facets[action.value], s: !state.facets[action.value]?.s},
                },
            }
            // Trigger select/deselect for Filters by checking newState.facets[action.value].s to see if it's selected or deselected
            // Then split newState.facets[action.value].v by ":" [1] the facet
            if (newState.facets[action.value].s) {
                const splitFilterName = newState.facets[action.value].v.split(":")
                TrackFacetSelection(splitFilterName[0], splitFilterName[1])
            } else {
                const splitFilterName = newState.facets[action.value].v.split(":")
                TrackFacetDeselection(splitFilterName[0], splitFilterName[1])
            }

            return deselectAllFiltersIncompatibleWith(newState, action.value)
        }
        case SET_ALL_FACETS: {
            const newState: SearchState = {
                ...state,
                facets: {...state.facets, ...action.value.facets},
            }
            return newState
        }

        case SET_KEY_FACETS: {
            const newState: SearchState = {
                ...state,
                facets: {...state.facets, ...action.value.facets},
            }
            return newState
        }

        case SET_CLEAR_TYPE_FILTER: {
            const newFacets = Object.entries(state.facets).reduce((acc, [key, value]) => {
                const splitFilterName = key.split(":")[0]
                acc[key] = splitFilterName === action.payload ? {...value, s: false} : value
                return acc
            }, {})
            return {
                ...state,
                facets: newFacets,
            }
        }

        case SET_CLEAR_ALL_FILTER: {
            const newFacets = Object.entries(state.facets).reduce((acc, [key, value]) => {
                acc[key] = value.s ? {...value, s: false} : value
                return acc
            }, {})
            return {
                ...state,
                facets: newFacets,
            }
        }
        case SET_CLEAR_PRICE_FILTER: {
            const newFilters = {
                ...state.filters,
                Price: {
                    ...state.filters.Price,
                    selectedMin: (state.filters.Price as FilterPrice).min,
                    selectedMax: (state.filters.Price as FilterPrice).max,
                },
            }
            return {
                ...state,
                filters: newFilters,
            }
        }
        case SET_SEO_FILTERS: {
            return {
                ...state,
                seoFilters: action.data,
            }
        }
        case SET_SEARCH_DATA: {
            const filterPrice = action.data?.filters?.Price as FilterPrice

            const newState: SearchState = {
                ...state,
                ...action.data,
                isFetchingPageItems: false,
                ...(filterPrice
                    ? {
                          minPrice: filterPrice?.selectedMin ?? filterPrice?.min,
                          maxPrice: filterPrice?.selectedMax ?? filterPrice?.max,
                      }
                    : {}),
                // Set page items supercedes infinite scroll actions
                isFetchingNextPage: false,
                isFetchingPreviousPage: false,
                itemsRequestPending: false,
            }

            Object.entries(newState.facets).forEach(([key, value]) => {
                if (value.s) {
                    deselectAllFiltersIncompatibleWith(newState, key)
                }
            })
            return newState
        }
        case SET_SELECTED_SORTING: {
            return {
                ...state,
                sorting: {
                    ...state.sorting,
                    selected: action.value,
                },
            }
        }
        case SET_FILTER_IS_OPEN: {
            const newFilters = {
                ...state.filters,
                [action.data.name]: {
                    ...state.filters[action.data.name],
                    isFilterOpen: action.data.isOpen,
                    ...(isFilterFacet(state.filters[action.data.name]) && {
                        isViewMoreOpen: action.data.isViewMoreOpen,
                    }),
                },
            }
            return {
                ...state,
                filters: newFilters,
            }
        }
        case SET_SELECTED_PRICE_FILTER: {
            const newFilters = {
                ...state.filters,
                [action.data.facetName]: {
                    ...state.filters[action.data.facetName],
                    selectedMin: action.data.selectedMin,
                    selectedMax: action.data.selectedMax,
                },
            }
            TrackPriceFilterChange(action.data.selectedMin, action.data.selectedMax)
            return {
                ...state,
                filters: newFilters,
            }
        }
        case SET_PAGE_ITEMS: {
            return {
                ...state,
                items: action.items,
                isFetchingPageItems: false,
                // Set page items supercedes infinite scroll actions
                isFetchingNextPage: false,
                isFetchingPreviousPage: false,
            }
        }
        case SET_NEW_PAGE_ITEMS: {
            return {
                ...state,
                items: action.items,
            }
        }
        case SET_IS_FETCHING_PAGE_ITEMS: {
            return {
                ...state,
                isFetchingPageItems: action.value,
                // Set page items supercedes infinite scroll actions
                isFetchingNextPage: false,
                isFetchingPreviousPage: false,
            }
        }
        case SET_ITEMS_REQUEST_PENDING: {
            return {
                ...state,
                itemsRequestPending: action.value,
            }
        }
        case NEXT_PAGE_ITEMS_READY: {
            return {
                ...state,
                items: [...state.items, ...action.items],
                endPage: action.currentPage,
                isFetchingNextPage: false,
            }
        }
        case PREVIOUS_PAGE_ITEMS_READY: {
            return {
                ...state,
                items: [...action.items, ...state.items],
                startPage: action.currentPage,
                isFetchingPreviousPage: false,
            }
        }
        case SET_IS_FETCHING_NEXT_PAGE: {
            return {
                ...state,
                isFetchingNextPage: action.value,
            }
        }
        case SET_IS_FETCHING_PREVIOUS_PAGE: {
            return {
                ...state,
                isFetchingPreviousPage: action.value,
            }
        }
        case SET_INITIAL_PAGE_RANGE_FROM_PAGE: {
            const newState = {
                ...state,
                ...mapToInitialPageRange(action.page),
            }
            return newState
        }
        case SET_DEBOUNCE_TIME: {
            return {
                ...state,
                debounceTime: action.time,
            }
        }
        case SET_INJECTED_ADS_ENABLED: {
            return {
                ...state,
                injectedAdsConfig: action.injectedAdsConfig,
            }
        }
        case SET_INJECTED_ADS_HIDDEN_FOR_TIMEOUT: {
            return {
                ...state,
                injectedAdHiddenForTimeout: action.injectedAdHiddenForTimeout,
            }
        }
        case SET_SINGLE_OPTION_FACET_LIST: {
            return {
                ...state,
                singleOptionFacetList: action.data,
            }
        }
        case SET_SINGLE_FACET_FILTERS_TO_BE_PRESERVED_IN_THE_URL: {
            return {
                ...state,
                singleFacetFiltersToBePreservedInTheUrl: action.data,
            }
        }
        case FIX_FACETS: {
            return {
                ...state,
                shouldFacetsBeFixed: true,
            }
        }
        case UNFIX_FACETS: {
            return {
                ...state,
                shouldFacetsBeFixed: false,
            }
        }
        case ASSIGN_SEARCH_STATE: {
            // Please do not any additional functionality!!!

            return {
                ...state,
                ...action.state,
            }
        }
        case SET_CURRENT_BREAKPOINT: {
            return {
                ...state,
                currentBreakpoint: action.payload,
            }
        }
        case SET_BLOOMREACH_COOKIES_INIT_LOAD: {
            return {
                ...state,
                bloomreachCookiesInitialVal: action.data,
            }
        }
        case SET_FILTERS: {
            return {
                ...state,
                filters: action.payload,
            }
        }
        case SET_IS_INITIAL_SCROLL_COMPLETED: {
            return {
                ...state,
                initialScrollCompleted: action.value,
            }
        }
        case SET_FIRST_ELEMENT_ID: {
            return {
                ...state,
                firstElementId: action.value,
            }
        }
        case SET_ITEMS_CACHE: {
            return {
                ...state,
                itemsCache: action.items,
            }
        }
        case SET_COOKIE_CONSENT_VALUE: {
            return {
                ...state,
                consentCookieValue: action.value,
            }
        }
        case SET_HIDE_FOOTER_UNTIL_LAST_PAGE: {
            return {
                ...state,
                hideFooterUntilLastPage: action.enabled,
            }
        }
        case SET_ENABLE_STATIC_ESI_ENDPOINT: {
            return {
                ...state,
                enableStaticEsiEndpoint: action.enabled,
            }
        }
        case SET_NUMBER_OF_FACETS_LOADED: {
            const newNumberOfFacetsLoaded = {
                ...state.numberOfFacetsLoaded,
                [action.filterName]:
                    (state.numberOfFacetsLoaded[action.filterName] || 0) +
                    FILTERS_SHOW_VIEW_MORE_COUNT_EXTENDED_HORIZONTAL,
            }
            return {
                ...state,
                numberOfFacetsLoaded: newNumberOfFacetsLoaded,
            }
        }
        case SET_FREE_TEXT_FILTER_SEARCH: {
            return {
                ...state,
                freeTextFilterSearch: {
                    ...state.freeTextFilterSearch,
                    [action.value.filterName]: action.value.value,
                },
            }
        }
        case SET_SUBSIDISE_ITEMS_FOR_LOAD_MORE: {
            return {
                ...state,
                subsidiseItemsForLoadMore: action.value,
            }
        }
        case SET_MIN_PRICE: {
            return {
                ...state,
                minPrice: action.value,
            }
        }
        case SET_MAX_PRICE: {
            return {
                ...state,
                maxPrice: action.value,
            }
        }
        case SET_SALE_CLEARANCE_SEARCH: {
            return {
                ...state,
                saleClearanceSearchTerm: action.term,
            }
        }
        default:
            return state
    }
}

export default reducer
